import {
  getLogisticsMilestoneOption,
  getLogisticsMilestoneOptions,
} from "./logisticsStageUpdateOptions";

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const isValidDateOnly = (value) => {
  if (!DATE_ONLY_PATTERN.test(value || "")) return false;

  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(year, month - 1, day);

  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
};

export const getTodayDateValue = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export const getLogisticsMilestoneDetailConfig = (stageKey, milestone) =>
  getLogisticsMilestoneOption(stageKey, milestone)?.detail || null;

export const getLogisticsMilestoneNextAction = (stageKey, milestone) => {
  if (!stageKey || String(stageKey).trim().toUpperCase() === "NOT_STARTED") {
    return (
      getLogisticsMilestoneOptions("NOT_STARTED")[0]?.nextAction ||
      "Review Progress"
    );
  }

  const option = getLogisticsMilestoneOption(stageKey, milestone);

  return option?.nextAction || "Review Progress";
};

export const getLogisticsMilestoneDetailError = (
  detailConfig,
  detailValue,
  today = getTodayDateValue(),
) => {
  if (!detailConfig) return "";

  const value = detailValue?.trim();
  if (!value) {
    return `${detailConfig.label} is required.`;
  }

  if (detailConfig.type !== "date") return "";

  if (!isValidDateOnly(value)) {
    return "Enter a valid date.";
  }

  if (detailConfig.dateRule === "todayOrFuture" && value < today) {
    return `${detailConfig.label} cannot be before today.`;
  }

  if (detailConfig.dateRule === "pastOrToday" && value > today) {
    return `${detailConfig.label} cannot be after today.`;
  }

  return "";
};

export const formatLogisticsDateOnly = (value) => {
  if (!isValidDateOnly(value)) return value || "";

  const [year, month, day] = value.split("-").map(Number);
  return new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(year, month - 1, day));
};

export const getLogisticsMilestoneDetailDisplay = ({
  stageKey,
  milestone,
  detailText,
  detailDate,
}) => {
  const detailConfig = getLogisticsMilestoneDetailConfig(stageKey, milestone);
  if (!detailConfig) return null;

  const rawValue =
    detailConfig.field === "detailDate" ? detailDate : detailText?.trim();
  if (!rawValue) return null;

  return {
    label: detailConfig.displayLabel || detailConfig.label,
    value:
      detailConfig.type === "date"
        ? formatLogisticsDateOnly(rawValue)
        : rawValue,
  };
};

export const getLogisticsProgressDetailRows = ({ detailText, detailDate }) => {
  const rows = [];
  const supplierName = detailText?.trim();

  if (supplierName) {
    rows.push({
      label: "Supplier Name",
      value: supplierName,
    });
  }

  if (detailDate) {
    rows.push({
      label: "Date",
      value: formatLogisticsDateOnly(detailDate),
    });
  }

  return rows;
};

export const getLogisticsMilestoneProgressDisplay = ({
  stageKey,
  milestone,
  detailText,
  detailDate,
}) => {
  const primaryValue = milestone || "No milestone started";
  const detailDisplay = getLogisticsMilestoneDetailDisplay({
    stageKey,
    milestone,
    detailText,
    detailDate,
  });

  return {
    primaryLabel: "Milestone",
    primaryValue,
    detailLabel: detailDisplay?.label || "",
    detailValue: detailDisplay?.value || "",
    detailRows: getLogisticsProgressDetailRows({ detailText, detailDate }),
    hidePrimaryWhenDetail: Boolean(detailDisplay),
    nextActionLabel: "Next",
    nextAction: getLogisticsMilestoneNextAction(stageKey, milestone),
  };
};
